          import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Avatar,

} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaUserFriends, 
  FaLock, 
  FaChartLine, 
  FaBrain,
  FaShieldAlt,
  FaHeart,
  FaBook,
  FaPen
} from 'react-icons/fa';
import { 
  RiEmotionLine, 
  RiTimeLine, 
  RiMailSendLine,
  RiMentalHealthLine, 
  RiBookmarkLine, 
  RiQuillPenLine,

} from 'react-icons/ri';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { EmailAnimation } from '../components/EmailAnimation';
import { Feature } from '../components/Feature';
import { StatBox } from '../components/StatBox';
import CountUp from 'react-countup';
import { Helmet } from 'react-helmet-async';
import Pricing from './Pricing';
import { Image } from '@chakra-ui/react';
import companyLogos from '../components/CompanyLogos';

const personalGrowthCases = [
  {
    title: "Daily Reflection",
    description: "Write about your day, challenges, and achievements. Your AI companion helps you identify patterns in your emotional responses and decision-making, offering insights for personal growth.",
    icon: RiEmotionLine,
    color: "blue.400"
  },
  {
    title: "Personal Growth Journey",
    description: "Document your goals and progress. The AI remembers your past aspirations, tracks your evolution, and helps you recognize how far you've come in your personal development.",
    icon: FaChartLine,
    color: "purple.400"
  },
  {
    title: "Memory Keeper",
    description: "Share life's meaningful moments - from small daily joys to major milestones. Your AI companion helps you build a rich, searchable archive of your life's journey.",
    icon: RiBookmarkLine,
    color: "green.400"
  },
  {
    title: "Creative Writing",
    description: "Use it as a writing companion for your creative thoughts, story ideas, or poetry. The AI maintains the context of your creative universe and helps develop your unique voice.",
    icon: RiQuillPenLine,
    color: "pink.400"
  },
  {
    title: "Learning Journal",
    description: "Document your learning experiences, insights, and questions. Your AI companion connects related concepts, reminds you of past learnings, and helps deepen your understanding.",
    icon: RiBookmarkLine,
    color: "yellow.400"
  },
  {
    title: "Mindfulness & Well-being",
    description: "Track your mental well-being, meditation insights, and emotional patterns. The AI helps you identify triggers, celebrate progress, and maintain a mindful practice.",
    icon: RiMentalHealthLine,
    color: "teal.400"
  }
];

const MotionBox = motion(Box);

const LandingPage = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const logoBg = useColorModeValue('white', 'gray.800');
  const logoFilter = useColorModeValue('none', 'brightness(0.8)');
  const logoColor = useColorModeValue('gray.700', 'gray.200');
  const cardBg = useColorModeValue('white', 'gray.700');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const [isLoading, setIsLoading] = useState(false);

  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      await signIn();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error signing in:', error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <Helmet>
        <title>Emailed | Your Smartest Email Contact</title>
        <meta name="description" content="Email your thoughts, memories, and questions to an AI that remembers everything." />
      </Helmet>
      <Box overflowX="hidden">
        <Header />
        
        {/* Hero Section - Moderate padding */}
        <Box 
          bg={useColorModeValue('gray.50', 'gray.900')} 
          pt={{ base: 16, md: 20 }} 
          pb={{ base: 12, md: 16 }}
          mt={{ base: 6, md: 8 }}
        >
          <Container maxW={'7xl'}>
            <Stack 
              direction={{ base: 'column', md: 'row' }} 
              align="center" 
              justify="space-between" 
              spacing={{ base: 10, md: 12 }}
              width="full"
            >
              {/* Left side: Text and CTA */}
              <Stack flex={1} spacing={{ base: 8, md: 10 }} align={{ base: 'center', md: 'flex-start' }}>
                <Stack spacing={4}>
                  <Heading
                    lineHeight={1.2}
                    fontWeight={600}
                    fontSize={{ base: '3xl', sm: '4xl', lg: '6xl' }}
                    textAlign={{ base: 'center', md: 'left' }}
                  >
                    <Text as={'span'}>
                      Your Personal
                    </Text>
                    <br />
                    <Text
                      as={'span'}
                      color={'blue.400'}
                    >
                      AI Memory Companion
                    </Text>
                  </Heading>

                  <Text 
                    color={'gray.500'} 
                    fontSize={{ base: 'lg', sm: 'xl' }} 
                    textAlign={{ base: 'center', md: 'left' }}
                    maxW={'540px'}
                  >
                    Share your thoughts, experiences, and questions. Your AI companion learns from every interaction, 
                    recognizes patterns, and helps you grow through meaningful insights.
                  </Text>
                </Stack>
                
                <Stack
                  spacing={{ base: 6, sm: 8 }}
                  pt={{ base: 4, md: 6 }}
                  align={{ base: 'center', md: 'flex-start' }}
                >
                  <VStack spacing={3}>
                    <HStack spacing={3} align="center">
                      <Button
                        rounded={'full'}
                        size={'lg'}
                        fontWeight={'bold'}
                        px={8}
                        py={7}
                        colorScheme={'blue'}
                        bg={'blue.400'}
                        _hover={{ bg: 'blue.500', transform: 'translateY(-2px)', boxShadow: 'lg' }}
                        _active={{ bg: 'blue.600' }}
                        transition="all 0.2s"
                        leftIcon={<FcGoogle />}
                        onClick={handleSignIn}
                        isLoading={isLoading}
                        loadingText="Signing in..."
                      >
                        <Text display={{ base: 'none', sm: 'block' }}>Start with 3 Free Emails/Day</Text>
                        <Text display={{ base: 'block', sm: 'none' }}>Start Free</Text>
                      </Button>
                      <Text fontSize="sm" color="gray.400">
                        Start with our free plan
                      </Text>
                    </HStack>
                    <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" fontWeight="medium">
                      Join 1000+ users unlocking personal growth through AI-powered memory insights
                    </Text>
                  </VStack>
                </Stack>
              </Stack>

              {/* Right side: Animation */}
              <Box 
                flex={1} 
                display="block"
                width="full"
                position="relative"
                pt={{ base: 6, md: 0 }}
                px={{ base: 4, md: 0 }}
                minHeight={{ base: '350px', md: '400px' }}
              >
                <EmailAnimation />
              </Box>
            </Stack>

            {/* Quick Social Proof - Compact supporting content */}
            <Box py={{ base: 8, md: 10 }} mt={{ base: 4, md: 6 }}>
              <Container maxW={'7xl'}>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 6, md: 8 }}>
                  <Box 
                    p={6} 
                    textAlign={'center'}
                    bg={useColorModeValue('white', 'gray.800')}
                    rounded={'lg'}
                    shadow={'xl'}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
                    transition="all 0.3s"
                  >
                    <Text fontSize={'4xl'} fontWeight={'bold'} mb={2}>
                      GPT-4
                    </Text>
                    <Text color={'gray.600'}>Advanced AI Model</Text>
                  </Box>
                  <Box 
                    p={6} 
                    textAlign={'center'}
                    bg={useColorModeValue('white', 'gray.800')}
                    rounded={'lg'}
                    shadow={'xl'}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
                    transition="all 0.3s"
                  >
                    <Text fontSize={'4xl'} fontWeight={'bold'} mb={2}>
                      Claude
                    </Text>
                    <Text color={'gray.600'}>Anthropic AI</Text>
                  </Box>
                  <Box 
                    p={6} 
                    textAlign={'center'}
                    bg={useColorModeValue('white', 'gray.800')}
                    rounded={'lg'}
                    shadow={'xl'}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
                    transition="all 0.3s"
                  >
                    <Text fontSize={'4xl'} fontWeight={'bold'} mb={2}>
                      Gemini
                    </Text>
                    <Text color={'gray.600'}>Google Vertex AI</Text>
                  </Box>
                  <Box 
                    p={6} 
                    textAlign={'center'}
                    bg={useColorModeValue('white', 'gray.800')}
                    rounded={'lg'}
                    shadow={'xl'}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
                    transition="all 0.3s"
                  >
                    <Text fontSize={'4xl'} fontWeight={'bold'} mb={2}>
                      <CountUp end={100} duration={2} suffix="%" />
                    </Text>
                    <Text color={'gray.600'}>Secure & Private</Text>
                  </Box>
                </SimpleGrid>
              </Container>
            </Box>

            {/* How It Works - Balanced spacing */}
            <Box 
              py={{ base: 12, md: 16 }} 
              bg={useColorModeValue('white', 'gray.800')}
            >
              <Container maxW={'7xl'}>
                <VStack spacing={{ base: 3, md: 4 }} mb={{ base: 8, md: 10 }} textAlign="center">
                  <Heading fontSize={'4xl'}>Your Growth Journey</Heading>
                  <Text color={'gray.600'} fontSize={'xl'} maxW={'2xl'}>
                    Three steps to unlock personal insights and continuous growth
                  </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 8, md: 10 }} mt={20}>
                  <VStack 
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    p={{ base: 6, md: 8 }}
                    rounded="xl"
                    align="flex-start"
                    spacing={4}
                  >
                    <Box
                      p={2}
                      bg="blue.400"
                      rounded="lg"
                      color="white"
                    >
                      <Icon as={RiMailSendLine} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
                    </Box>
                    <Text fontWeight="bold" fontSize={{ base: "lg", md: "xl" }}>1. Connect With Your AI</Text>
                    <Text color={'gray.600'} fontSize={{ base: "sm", md: "md" }}>
                      Start a conversation with your AI companion through email. Share your thoughts, 
                      experiences, and questions - just like writing to a trusted friend who never forgets.
                    </Text>
                  </VStack>

                  <VStack 
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    p={{ base: 6, md: 8 }}
                    rounded="xl"
                    align="flex-start"
                    spacing={4}
                  >
                    <Box
                      p={2}
                      bg="green.400"
                      rounded="lg"
                      color="white"
                    >
                      <Icon as={FaBrain} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
                    </Box>
                    <Text fontWeight="bold" fontSize={{ base: "lg", md: "xl" }}>2. Unlock Personal Insights</Text>
                    <Text color={'gray.600'} fontSize={{ base: "sm", md: "md" }}>
                      Your AI companion analyzes patterns in your communications, recognizes life events, 
                      and connects past experiences with current situations to provide meaningful insights.
                    </Text>
                  </VStack>

                  <VStack 
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    p={{ base: 6, md: 8 }}
                    rounded="xl"
                    align="flex-start"
                    spacing={4}
                  >
                    <Box
                      p={2}
                      bg="purple.400"
                      rounded="lg"
                      color="white"
                    >
                      <Icon as={RiTimeLine} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
                    </Box>
                    <Text fontWeight="bold" fontSize={{ base: "lg", md: "xl" }}>3. Grow Together</Text>
                    <Text color={'gray.600'} fontSize={{ base: "sm", md: "md" }}>
                      As your AI learns more about your journey, it provides increasingly personalized guidance, 
                      helping you make better decisions based on your past experiences and growth patterns.
                    </Text>
                  </VStack>
                </SimpleGrid>

                {/* Company Logos - Compact grid */}
                {/* Removed this section */}
              </Container>
            </Box>

            {/* What Users Say */}
            <Box 
              py={{ base: 8, md: 16 }} 
              px={{ base: 4, md: 8 }}
              bg={useColorModeValue('gray.50', 'gray.800')}
            >
              <Container maxW={'7xl'}>
                <VStack spacing={{ base: 3, md: 4 }} mb={{ base: 8, md: 10 }} textAlign="center">
                  <Heading fontSize={'4xl'}>Your Personal Growth Journey</Heading>
                  <Text color={'gray.600'} fontSize={'xl'} maxW={'2xl'}>
                    Discover the many ways you can use your AI companion for personal growth and self-reflection
                  </Text>
                </VStack>

                {/* Personal Growth Use Cases */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} px={{ base: 4, md: 8 }} maxW={'7xl'} mx="auto" mb={{ base: 10, md: 12 }}>
                  {personalGrowthCases.map((useCase, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Box
                        bg={cardBg}
                        p={6}
                        rounded={'xl'}
                        shadow={'xl'}
                        height="full"
                        position="relative"
                        _hover={{
                          transform: 'translateY(-5px)',
                          shadow: '2xl',
                          transition: 'all 0.2s'
                        }}
                      >
                        <Flex direction="column" height="full">
                          <Icon
                            as={useCase.icon}
                            w={8}
                            h={8}
                            mb={4}
                            color={useCase.color}
                          />
                          <Heading
                            size="md"
                            mb={4}
                            color={headingColor}
                          >
                            {useCase.title}
                          </Heading>
                          <Text
                            color={textColor}
                            fontSize="md"
                            flex="1"
                          >
                            {useCase.description}
                          </Text>
                        </Flex>
                      </Box>
                    </MotionBox>
                  ))}


</SimpleGrid>


              </Container>
            </Box>

            {/* Trust Section - Compact but clear */}
            <Box py={{ base: 12, md: 16 }} bg={useColorModeValue('white', 'gray.800')}>
              <Container maxW={'7xl'}>
                <VStack spacing={{ base: 3, md: 4 }} as={Container} maxW={'3xl'} textAlign={'center'} mb={{ base: 8, md: 10 }}>
                  <Heading fontSize={'4xl'}>Trust & Security</Heading>
                  <Text color={'gray.600'} fontSize={'xl'}>
                    Your privacy and security are our top priorities. We employ enterprise-grade
                    security measures to protect your data.
                  </Text>
                </VStack>
                <SimpleGrid 
                  columns={{ base: 1, md: 3 }} 
                  spacing={{ base: 6, md: 10 }}
                  px={{ base: 4, md: 0 }}
                >
                  <Feature
                    icon={<Icon as={FaShieldAlt} w={10} h={10} />}
                    title={'Enterprise-Grade Security'}
                    text={'Your data is protected using industry-standard encryption and secure infrastructure. We implement robust security measures to safeguard your information.'}
                    delay={0.2}
                  />
                  <Feature
                    icon={<Icon as={FaUserFriends} w={10} h={10} />}
                    title={'User Privacy Control'}
                    text={'You have full control over your data. Access, export, or delete your information at any time.'}
                    delay={0.6}
                  />
                  <Feature
                    icon={<Icon as={FaChartLine} w={10} h={10} />}
                    title={'Regular Security Audits'}
                    text={'We regularly perform security audits to ensure our systems are secure and up-to-date.'}
                    delay={0.8}
                  />
                </SimpleGrid>
              </Container>
            </Box>

            {/* Pricing Section */}
            <Pricing />

            {/* CTA Section - Impactful but not excessive */}
            <Box py={{ base: 12, md: 16 }} bg={bg}>
              <Container maxW={'7xl'}>
                <Stack
                  as={Box}
                  textAlign={'center'}
                  spacing={{ base: 6, md: 8 }}
                  pb={{ base: 8, md: 10 }}
                >
                  <Heading
                    fontWeight={600}
                    fontSize={{ base: '3xl', sm: '4xl', md: '6xl' }}
                    lineHeight={'110%'}
                  >
                    Begin Your Journey with <br />
                    <Text as={'span'} color={'blue.400'}>
                      Your AI Memory Companion
                    </Text>
                  </Heading>
                  <Text color={'gray.600'} maxW={'3xl'} fontSize={'xl'} margin={'auto'}>
                    Start with 3 free emails per day. Experience how an AI that understands your journey can help you grow through meaningful insights.
                  </Text>
                  <Stack
                    direction={'column'}
                    spacing={{ base: 4, md: 3 }}
                    align={'center'}
                    alignSelf={'center'}
                    position={'relative'}
                    px={{ base: 4, md: 0 }}
                  >
                    <Button
                      size="lg"
                      colorScheme="blue"
                      leftIcon={<FcGoogle />}
                      onClick={handleSignIn}
                      px={8}
                      fontSize="md"
                      isLoading={isLoading}
                      loadingText="Signing in..."
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
                      transition="all 0.2s"
                    >
                      Start with 3 Free Emails/Day
                    </Button>
                    <Text fontSize={'sm'} color={'gray.500'}>
                      Start with our free plan
                    </Text>
                  </Stack>
                </Stack>
              </Container>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LandingPage;
